import React from 'react';
import {
    format,
    eachMonthOfInterval,
    startOfYear,
    endOfYear,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    getDate,
    isWeekend
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { Technician } from '@shared/types';
import { isWorkDay } from '@shared/utils/schedule';

interface YearViewProps {
    currentDate: Date;
    technicians: Technician[];
    showWeekends: boolean;
}

export const YearView: React.FC<YearViewProps> = ({ currentDate, technicians, showWeekends }) => {
    const yearStart = startOfYear(currentDate);
    const yearEnd = endOfYear(currentDate);
    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

    return (
        <div className="glass-panel p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {months.map((month, monthIdx) => (
                    <MiniMonth
                        key={month.toISOString()}
                        month={month}
                        technicians={technicians}
                        idx={monthIdx}
                        showWeekends={showWeekends}
                    />
                ))}
            </div>
        </div>
    );
};

const MiniMonth: React.FC<{ month: Date; technicians: Technician[]; idx: number; showWeekends: boolean }> = ({ month, technicians, idx, showWeekends }) => {
    // Actually we need end of month
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const calendarStart = startOfWeek(month, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    // Config based on showWeekends
    const weekDays = showWeekends
        ? ['L', 'M', 'M', 'J', 'V', 'S', 'D']
        : ['L', 'M', 'M', 'J', 'V'];

    const gridColsClass = showWeekends ? 'grid-cols-7' : 'grid-cols-5';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="border border-glass-border rounded-xl p-4"
        >
            <h3 className="text-center font-semibold mb-3 capitalize text-main">
                {format(month, 'MMMM', { locale: fr })}
            </h3>

            <div className={`grid ${gridColsClass} gap-1 text-center mb-1`}>
                {weekDays.map(d => (
                    <span key={d} className="text-[10px] text-muted font-medium">{d}</span>
                ))}
            </div>

            <div className={`grid ${gridColsClass} gap-1`}>
                {days.map((day) => {
                    const isCurrent = isSameMonth(day, month);

                    // Filter weekends if needed
                    if (!showWeekends && isWeekend(day)) return null;

                    if (!isCurrent) return <div key={day.toISOString()} />;

                    const workingCount = technicians.filter(t => isWorkDay(day, t)).length;

                    let bgClass = 'bg-glass-border/10 text-muted';
                    if (workingCount > 0) {
                        // Use higher opacity (80-90%) to make white text readable on light backgrounds
                        if (workingCount === 1) bgClass = 'bg-primary/90 text-white shadow-sm';
                        else if (workingCount === 2) bgClass = 'bg-accent/90 text-white shadow-sm';
                        else bgClass = 'bg-emerald-500/90 text-white shadow-sm';
                    }

                    return (
                        <div
                            key={day.toISOString()}
                            className={`aspect-square rounded-full flex items-center justify-center text-xs cursor-default hover:scale-110 transition-transform ${bgClass}`}
                            title={`${format(day, 'dd MMM')}: ${workingCount} technicien(s)`}
                        >
                            <span className="text-[10px]">{getDate(day)}</span>
                        </div>
                    );
                })}
            </div>
        </motion.div>
    );
};
